import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { usePerformanceLab } from "@/contexts/PerformanceLabContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function LabSaveModal({ open, onOpenChange }: Props) {
  const { translate } = useTranslation();
  const { session, setSessionNote } = usePerformanceLab();
  const { currentOrganization } = useOrganization();
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(session.sessionNote);

  // Derive promotable lineups: fixture IDs that have at least one lineup entry
  const promotableFixtureIds = [
    ...new Set(session.lineups.map((l) => l.fixture_id)),
  ];
  const promotableFixtures = session.fixtures.filter((f) =>
    promotableFixtureIds.includes(f.id),
  );

  const hasNote = note.trim().length > 0;
  const hasLineups = promotableFixtures.length > 0;
  const nothingToSave = !hasLineups && !hasNote;

  const handleSave = async () => {
    if (nothingToSave) return;
    setSaving(true);
    analytics.track("lab.save_initiated");

    const savedItems: string[] = [];
    try {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error("No organisation");

      // Promote draft lineups
      for (const fixture of promotableFixtures) {
        const entries = session.lineups.filter(
          (l) => l.fixture_id === fixture.id && !l.player_id.startsWith("hypo-"),
        );
        if (entries.length === 0) continue;

        const lineupRows = entries.map((e) => ({
          match_id: fixture.id,
          player_id: e.player_id,
          role: e.position_slot.startsWith("SUB") ? "SUB" : "STARTER",
          position: e.position_slot,
        }));

        // Upsert into match_lineups (draft — not confirmed selection)
        // We delete existing draft entries first then insert
        await supabase
          .from("match_lineups")
          .delete()
          .eq("match_id", fixture.id);
        await supabase.from("match_lineups").insert(lineupRows);
        savedItems.push("lineup");
      }

      // Promote session note to first fixture's briefing notes if applicable
      if (hasNote && promotableFixtures.length > 0) {
        const fixtureId = promotableFixtures[0].id;
        await supabase
          .from("matches")
          .update({ notes: note })
          .eq("id", fixtureId)
          .eq("organization_id", orgId);
        savedItems.push("note");
      }

      setSessionNote(note);
      analytics.track("lab.save_completed", { items_saved: [...new Set(savedItems)].join(",") });
      toast.success(translate("lab_save_success"));
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(translate("lab_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{translate("lab_save_modal_title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {translate("lab_save_modal_subtitle")}
          </p>

          {/* Promotable lineups */}
          {promotableFixtures.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translate("lab_save_lineups_heading")}
              </p>
              {promotableFixtures.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
                >
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  <div>
                    <p className="font-medium">{f.opponent}</p>
                    <p className="text-xs text-muted-foreground">
                      {translate("lab_save_draft_lineup_note")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Session note */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {translate("lab_save_note_label")}
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={translate("lab_save_note_placeholder")}
              className="min-h-[80px] text-sm"
            />
            {hasNote && (
              <p className="text-xs text-muted-foreground">
                {translate("lab_save_note_destination")}
              </p>
            )}
          </div>

          {nothingToSave && (
            <p className="text-sm text-muted-foreground text-center py-2">
              {translate("lab_save_nothing")}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {translate("lab_save_cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving || nothingToSave}>
            {saving ? translate("lab_save_saving") : translate("lab_save_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
